import React from 'react';
import {
  cssColorValue,
  List,
  ListItem,
  screenSmMin,
  semanticColors,
  spacer32,
  spacer40,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import { DocumentLink, SanityArticleData } from 'lib/sanity/types';
import IconChevron from 'components/IconChevron';
import Ellipsis from 'components/Ellipsis';
import { HomeLink, ParentCategoryLink } from 'components/Links';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { UbiParentCategoryPage } from 'utils/ubi/pages/useUbiParentCategory';

const StyledList = styled(List)`
  display: flex;
  flex-direction: row;
  margin-block-end: ${spacer32};
  padding: 0;
  color: ${cssColorValue(semanticColors.textSubdued)};
  width: 100%;

  @media (min-width: ${screenSmMin}) {
    margin-block-end: ${spacer40};
  }
`;

const TextNoWrap = styled(Type)`
  white-space: nowrap;
`;

const ListItemWithEllipsis = styled(ListItem)`
  overflow: hidden;
`;

const LinkWithEllipsis = styled(ParentCategoryLink)`
  width: 100%;
`;

const Separator = styled(ListItem)`
  margin: 0 ${spacer8};
  display: inline-flex;
  align-items: center;
`;

interface BreadcrumbsProps {
  parentCategory: DocumentLink;
  article?: SanityArticleData;
  ubi: UbiArticlePage | UbiParentCategoryPage;
}

const Breadcrumbs = ({ parentCategory, article, ubi }: BreadcrumbsProps) => {
  const { t } = useTranslation();

  // Since Sanity document relationship is handled through the english master,
  // if the parentcategory is not translated, but the article is published
  // the title can be empty, and then we should not render the breadcrumb.
  const { title: articleTitle } = article ?? {};

  const { title: categoryTitle, slug: categorySlug } = parentCategory ?? {};

  return (
    <nav aria-label={t('breadcrumbs')}>
      <StyledList>
        <ListItem>
          <HomeLink ubiFactory={ubi.logBreadcrumbHomeLink()} standalone>
            <TextNoWrap variant="ballad" weight="bold" data-testid="breadcrumbHome">
              {t('breadcrumb_home')}
            </TextNoWrap>
          </HomeLink>
        </ListItem>
        <Separator role="separator">
          <IconChevron size={16} />
        </Separator>
        {categorySlug &&
          categoryTitle &&
          (articleTitle ? (
            <>
              <ListItemWithEllipsis>
                <LinkWithEllipsis
                  parentCategorySlug={categorySlug}
                  data-testid="breadcrumbCategory"
                  standalone
                  ubiFactory={(ubi as UbiArticlePage).logBreadcrumbLink?.(categorySlug)}
                >
                  <Ellipsis variant="ballad" weight="bold">
                    {categoryTitle}
                  </Ellipsis>
                </LinkWithEllipsis>
              </ListItemWithEllipsis>
              <Separator role="separator">
                <IconChevron size={16} />
              </Separator>
              <ListItemWithEllipsis aria-hidden="true" hidden aria-current="page">
                <Ellipsis variant="ballad" weight="bold">
                  {articleTitle}
                </Ellipsis>
              </ListItemWithEllipsis>
            </>
          ) : (
            <ListItemWithEllipsis aria-hidden="true" hidden aria-current="page">
              <Ellipsis variant="ballad" weight="bold">
                {categoryTitle}
              </Ellipsis>
            </ListItemWithEllipsis>
          ))}
      </StyledList>
    </nav>
  );
};

export default Breadcrumbs;
